import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ExchangeRegisterBanner from '../components/ExchangeRegisterBanner';
import {
  getExchangeInviteCode,
  getRebateExchange,
  rebateExchanges,
} from '../data/rebateData';
import { useLang } from '../i18n/context';
import './RebatePage.css';

function RebateDetailPage() {
  const { exchange: exchangeId } = useParams();
  const exchange = getRebateExchange(exchangeId);
  const { t } = useLang();

  if (!exchange) {
    return <Navigate to="/rebate" replace />;
  }

  const registerDisabled = exchange.registerUrl === '#';
  const inviteCode = getExchangeInviteCode(exchange);

  return (
    <main className="rebate-page rebate-detail-page">
      <nav className="rebate-breadcrumb" aria-label={t('rebate.breadcrumb')}>
        <Link to="/">{t('rebate.home')}</Link>
        <span>/</span>
        <Link to="/rebate">{t('rebate.rebate')}</Link>
        <span>/</span>
        <strong>{exchange.name}</strong>
      </nav>

      <section className="rebate-detail-hero" style={{ '--exchange-accent': exchange.accent }}>
        <div className="rebate-detail-title">
          <div className="rebate-exchange-mark">
            <img src={exchange.logo} alt={`${exchange.name} logo`} />
          </div>
          <div>
            <p>{exchange.name}</p>
            <span>{exchange.badge}，{t('rebate.inviteCodeLabel')} {inviteCode}</span>
          </div>
        </div>
        <h1>{t('rebate.detailHeroTitle')}</h1>
        <p className="rebate-detail-desc">{exchange.detailSummary}</p>
        <div className="rebate-detail-actions">
          {registerDisabled ? (
            <a href="#steps" className="rebate-btn-primary">{t('rebate.viewSteps')}</a>
          ) : (
            <ExchangeRegisterBanner
              exchange={exchange}
              title={t('rebate.registerWithCode', { code: inviteCode })}
              description={`${exchange.shortName} 专属注册链接 · ${exchange.rebate} ${t('rebate.rebateLabel')}`}
              cta="前往注册"
              className="rebate-register-banner-hero"
            />
          )}
          <a href="#rebind-guide" className="rebate-btn-secondary">{t('rebate.viewRebindGuide')}</a>
        </div>
      </section>

      <section className="rebate-detail-grid">
        <div className="rebate-detail-main">
          <section className="rebate-detail-section">
            <div className="rebate-section-head compact">
              <p>{exchange.name}</p>
              <h2>{t('rebate.whyChoose')}</h2>
            </div>
            <div className="rebate-feature-list">
              {exchange.strengths.map(([title, desc]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="rebind-guide" className="rebate-detail-section">
            <div className="rebate-section-head compact">
              <p>{t('rebate.rebindSubtitle')}</p>
              <h2>{t('rebate.rebindGuideTitle')}</h2>
            </div>
            <div className="rebate-timeline">
              {exchange.rebind.map(([title, desc], index) => (
                <article key={title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section id="steps" className="rebate-detail-section">
            <div className="rebate-section-head compact">
              <p>{t('rebate.newUserRegister')}</p>
              <h2>{t('rebate.stepsTitle')}</h2>
            </div>
            <div className="rebate-step-list">
              {exchange.steps.map(([title, desc], index) => (
                <article key={title}>
                  <strong>{index + 1}</strong>
                  <div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </article>
              ))}
            </div>
            {!registerDisabled && (
              <ExchangeRegisterBanner
                exchange={exchange}
                title={t('rebate.openRegister', { name: exchange.shortName })}
                description={`注册前确认邀请码 ${inviteCode}，保存 UID 便于后续核对。`}
                cta="打开链接"
                className="rebate-register-banner-final"
              />
            )}
          </section>

          <section className="rebate-detail-section">
            <div className="rebate-section-head compact">
              <p>{t('rebate.extraBenefits')}</p>
              <h2>{t('rebate.checkAfterRegister')}</h2>
            </div>
            <div className="rebate-benefit-grid">
              {exchange.benefits.map(([title, desc]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="rebate-detail-aside">
          <div className="rebate-sticky-card">
            <span>{t('rebate.inviteCodeLabel')}</span>
            <strong>{inviteCode}</strong>
            <p>{exchange.name} · {exchange.rebate} {t('rebate.rebateLabel')}</p>
            {!registerDisabled && (
              <ExchangeRegisterBanner
                exchange={exchange}
                title={t('rebate.directRegister')}
                description={`邀请码 ${inviteCode}`}
                cta="前往"
                className="rebate-register-banner-sticky"
              />
            )}
          </div>

          <div className="rebate-faq-card">
            <h2>{t('rebate.faqTitle')}</h2>
            {exchange.faq.map(([question, answer]) => (
              <details key={question} open>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>

          <div className="rebate-other-card">
            <h2>{t('rebate.otherPlatforms')}</h2>
            {rebateExchanges.filter((item) => item.id !== exchange.id).map((item) => (
              <Link to={`/rebate/${item.id}`} key={item.id}>
                <span><img src={item.logo} alt="" />{item.name}</span>
                <strong>{item.rebate}</strong>
              </Link>
            ))}
          </div>
        </aside>
      </section>
    </main>
  );
}

export default RebateDetailPage;
